import React, { useState, useEffect, FormEvent } from 'react';
import {
  ChangePasswordRequest,
  validatePasswordPolicy,
  PasswordPolicyViolationType,
  NotificationPreferencesResponse,
  UpdateNotificationPreferencesRequest,
} from '@df-gate-share/shared';
import { useAuth } from '../contexts/AuthContext';
import { apiClient, getErrorMessage, ERROR_MESSAGES } from '../lib/api';
import Header from '../components/Header';
import axios from 'axios';

const POLICY_LABELS: Record<PasswordPolicyViolationType, string> = {
  minLength: '8文字以上',
  maxLength: '128文字以内',
  uppercase: '大文字(A-Z)',
  lowercase: '小文字(a-z)',
  digit: '数字(0-9)',
  symbol: '記号(!@#$%...)',
  sameAsCurrent: '現在のパスワードと異なる',
};

const POLICY_CHECKS: PasswordPolicyViolationType[] = [
  'minLength',
  'maxLength',
  'uppercase',
  'lowercase',
  'digit',
  'symbol',
];

const Settings: React.FC = () => {
  const { user } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentPasswordError, setCurrentPasswordError] = useState('');
  const [apiError, setApiError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // 通知設定（メール配信オプトアウト, Bug-022）
  const [prefs, setPrefs] = useState<NotificationPreferencesResponse | null>(null);
  const [prefsLoading, setPrefsLoading] = useState(true);
  const [prefsError, setPrefsError] = useState('');
  const [savingPref, setSavingPref] = useState<keyof NotificationPreferencesResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setPrefsLoading(true);
      setPrefsError('');
      try {
        const res = await apiClient.get<NotificationPreferencesResponse>(
          '/api/v1/users/me/notification-preferences'
        );
        if (!cancelled) setPrefs(res.data);
      } catch (error) {
        if (!cancelled) setPrefsError(getErrorMessage(error));
      } finally {
        if (!cancelled) setPrefsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const updatePreference = async (
    key: keyof NotificationPreferencesResponse,
    value: boolean
  ) => {
    if (!prefs) return;
    setSavingPref(key);
    setPrefsError('');
    const previous = prefs;
    // 楽観的更新
    setPrefs({ ...prefs, [key]: value });
    try {
      const body: UpdateNotificationPreferencesRequest = { [key]: value };
      const res = await apiClient.put<NotificationPreferencesResponse>(
        '/api/v1/users/me/notification-preferences',
        body
      );
      setPrefs(res.data);
    } catch (error) {
      // 失敗時はロールバック
      setPrefs(previous);
      setPrefsError(getErrorMessage(error));
    } finally {
      setSavingPref(null);
    }
  };

  // Upd-003: パスワード変更可否は「パスワード設定済みか（hasPassword）」で判定する。
  // authMethod はアカウント作成方法を表す単一値であり、OIDC作成＋パスワード設定の
  // ハイブリッドユーザー（authMethod='oidc' でもパスワードあり）を誤って弾くため使わない。
  // hasPassword 未提供の古いセッション（localStorage）に対しては authMethod でフォールバック。
  const canChangePassword = user?.hasPassword ?? (user?.authMethod === 'email');

  // Real-time password policy validation
  const policyResult = validatePasswordPolicy(newPassword, currentPassword);
  const satisfiedPolicies = new Set(
    POLICY_CHECKS.filter((p) => !policyResult.violations.includes(p))
  );
  const isSameAsCurrent = policyResult.violations.includes('sameAsCurrent');

  // Password match check
  const passwordsMatch = newPassword === confirmPassword;
  const hasConfirmInput = confirmPassword.length > 0;
  const isFormValid =
    currentPassword.length > 0 &&
    policyResult.valid &&
    !isSameAsCurrent &&
    passwordsMatch &&
    hasConfirmInput;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setApiError('');
    setCurrentPasswordError('');
    setIsSuccess(false);

    if (!isFormValid) {
      return;
    }

    setIsLoading(true);

    try {
      const requestBody: ChangePasswordRequest = {
        currentPassword,
        newPassword,
      };

      await apiClient.put('/api/v1/auth/password', requestBody);
      setIsSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        const errorCode = error.response?.data?.error?.code;
        const status = error.response?.status;

        if (status === 401 && errorCode === 'CURRENT_PASSWORD_INCORRECT') {
          setCurrentPasswordError(ERROR_MESSAGES.CURRENT_PASSWORD_INCORRECT);
        } else if (status === 400 && errorCode === 'PASSWORD_POLICY_VIOLATION') {
          setApiError(ERROR_MESSAGES.PASSWORD_POLICY_VIOLATION);
        } else if (status === 429 && errorCode === 'PASSWORD_CHANGE_RATE_LIMITED') {
          setApiError(ERROR_MESSAGES.PASSWORD_CHANGE_RATE_LIMITED);
        } else {
          setApiError(getErrorMessage(error));
        }
      } else {
        setApiError(getErrorMessage(error));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div className="page-wrapper">
        <div className="page-container page-container--narrow">
          <h1 className="page-title">設定</h1>

          <div className="card">
            <div className="card__header">
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-lg)', fontWeight: 700 }}>パスワード変更</h2>
            </div>
            <div className="card__body">
              {!canChangePassword ? (
                <p className="text-muted">
                  パスワードの変更は認証プロバイダーで行ってください
                </p>
              ) : (
                <>
                  {isSuccess && (
                    <div className="alert alert--success" role="alert">
                      パスワードが正常に変更されました
                    </div>
                  )}

                  {apiError && (
                    <div className="alert alert--error" role="alert">
                      {apiError}
                    </div>
                  )}

                  <form onSubmit={handleSubmit}>
                    <div className="form-group">
                      <label htmlFor="currentPassword" className="form-label">現在のパスワード</label>
                      <input
                        id="currentPassword"
                        type="password"
                        value={currentPassword}
                        onChange={(e) => {
                          setCurrentPassword(e.target.value);
                          setCurrentPasswordError('');
                        }}
                        disabled={isLoading}
                        placeholder="現在のパスワードを入力"
                        autoComplete="current-password"
                        className={`form-input ${currentPasswordError ? 'form-input--error' : ''}`}
                      />
                      {currentPasswordError && (
                        <p className="form-error">{currentPasswordError}</p>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="newPassword" className="form-label">新しいパスワード</label>
                      <input
                        id="newPassword"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        disabled={isLoading}
                        placeholder="新しいパスワードを入力"
                        autoComplete="new-password"
                        className="form-input"
                      />
                    </div>

                    {/* Password strength indicator */}
                    <div className="policy-list mb-4">
                      {POLICY_CHECKS.map((policy) => (
                        <div
                          key={policy}
                          data-testid="policy-item"
                          className={`policy-item ${satisfiedPolicies.has(policy) ? 'policy-item--satisfied' : ''}`}
                        >
                          <span className="policy-item__icon">
                            {satisfiedPolicies.has(policy) ? '✓' : '○'}
                          </span>
                          {POLICY_LABELS[policy]}
                        </div>
                      ))}
                      {isSameAsCurrent && (
                        <div data-testid="policy-item-same" className="policy-item policy-item--error">
                          <span className="policy-item__icon">✗</span>
                          {POLICY_LABELS.sameAsCurrent}
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="confirmPassword" className="form-label">新しいパスワード（確認）</label>
                      <input
                        id="confirmPassword"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={isLoading}
                        placeholder="パスワードを再入力"
                        autoComplete="new-password"
                        className={`form-input ${hasConfirmInput && !passwordsMatch ? 'form-input--error' : ''}`}
                      />
                      {hasConfirmInput && !passwordsMatch && (
                        <p className="form-error">パスワードが一致しません</p>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading || !isFormValid}
                      className="btn btn--primary btn--lg"
                    >
                      {isLoading ? '変更中...' : 'パスワードを変更'}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>

          {/* メール通知設定（配信オプトアウト, Bug-022） */}
          <div className="card mt-6">
            <div className="card__header">
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-lg)', fontWeight: 700 }}>メール通知設定</h2>
            </div>
            <div className="card__body">
              <p className="text-muted mb-4" style={{ fontSize: 'var(--text-sm)' }}>
                受信するメール通知の種類を設定できます。オフにすると該当メールが送信されなくなります。
              </p>

              {prefsError && (
                <div className="alert alert--error" role="alert">
                  {prefsError}
                </div>
              )}

              {prefsLoading ? (
                <p className="text-muted">読み込み中...</p>
              ) : prefs ? (
                <div className="flex flex-col gap-4">
                  <label className="flex items-center gap-3" style={{ cursor: savingPref ? 'wait' : 'pointer' }}>
                    <input
                      type="checkbox"
                      aria-label="お知らせメール"
                      checked={prefs.announcementEmail}
                      disabled={savingPref !== null}
                      onChange={(e) => updatePreference('announcementEmail', e.target.checked)}
                    />
                    <span>
                      <span className="form-label" style={{ margin: 0 }}>お知らせメール</span>
                      <span className="text-muted" style={{ display: 'block', fontSize: 'var(--text-sm)' }}>
                        メンテナンス・障害・新機能などのお知らせを受信します。
                      </span>
                    </span>
                  </label>

                  <label className="flex items-center gap-3" style={{ cursor: savingPref ? 'wait' : 'pointer' }}>
                    <input
                      type="checkbox"
                      aria-label="ファイル有効期限通知メール"
                      checked={prefs.expiryEmail}
                      disabled={savingPref !== null}
                      onChange={(e) => updatePreference('expiryEmail', e.target.checked)}
                    />
                    <span>
                      <span className="form-label" style={{ margin: 0 }}>ファイル有効期限通知メール</span>
                      <span className="text-muted" style={{ display: 'block', fontSize: 'var(--text-sm)' }}>
                        アップロードしたファイルの有効期限が近づいた際に通知します。
                      </span>
                    </span>
                  </label>
                </div>
              ) : (
                <p className="text-muted">通知設定を読み込めませんでした。</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Settings;
